import { useCallback, useRef, useState } from 'react';

const MAX_HISTORY = 50;

/**
 * Undo/redo hook using Fabric.js canvas serialization.
 * Maintains a history stack and redo stack of canvas JSON snapshots.
 */
export const useSmileDesignerHistory = (fabricCanvas) => {
  const historyStack = useRef([]);
  const redoStack = useRef([]);
  const isRestoring = useRef(false);
  const [historyLength, setHistoryLength] = useState(0);
  const [redoLength, setRedoLength] = useState(0);

  const saveState = useCallback(() => {
    if (!fabricCanvas || isRestoring.current) return;
    try {
      const json = JSON.stringify(fabricCanvas.toJSON(['customType', 'toothType', 'toothVariant', 'side', 'toothIndex', 'globalCompositeOperation']));
      historyStack.current.push(json);
      if (historyStack.current.length > MAX_HISTORY) {
        historyStack.current.shift();
      }
      redoStack.current = [];
      setHistoryLength(historyStack.current.length);
      setRedoLength(0);
    } catch (e) {
      console.error('Failed to save canvas state:', e);
    }
  }, [fabricCanvas]);

  const undo = useCallback(() => {
    if (!fabricCanvas || historyStack.current.length === 0) return;
    isRestoring.current = true;

    const currentState = JSON.stringify(fabricCanvas.toJSON(['customType', 'toothType', 'toothVariant', 'side', 'toothIndex', 'globalCompositeOperation']));
    redoStack.current.push(currentState);

    const previousState = historyStack.current.pop();
    fabricCanvas.loadFromJSON(previousState, () => {
      fabricCanvas.renderAll();
      isRestoring.current = false;
      setHistoryLength(historyStack.current.length);
      setRedoLength(redoStack.current.length);
    });
  }, [fabricCanvas]);

  const redo = useCallback(() => {
    if (!fabricCanvas || redoStack.current.length === 0) return;
    isRestoring.current = true;

    const currentState = JSON.stringify(fabricCanvas.toJSON(['customType', 'toothType', 'toothVariant', 'side', 'toothIndex', 'globalCompositeOperation']));
    historyStack.current.push(currentState);

    const nextState = redoStack.current.pop();
    fabricCanvas.loadFromJSON(nextState, () => {
      fabricCanvas.renderAll();
      isRestoring.current = false;
      setHistoryLength(historyStack.current.length);
      setRedoLength(redoStack.current.length);
    });
  }, [fabricCanvas]);

  const clearHistory = useCallback(() => {
    historyStack.current = [];
    redoStack.current = [];
    setHistoryLength(0);
    setRedoLength(0);
  }, []);

  return {
    saveState,
    undo,
    redo,
    clearHistory,
    canUndo: historyLength > 0,
    canRedo: redoLength > 0,
  };
};
